import Builder from './builder'
import { toast } from 'react-toastify'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import React, { PureComponent } from 'react'
import Loading from 'components/message/loading'
import { RouteComponentProps } from 'react-router'
import ErrorActions from 'app/redux/actions/core/errors'
import BusinessActions from 'app/redux/actions/pages/business'
import DatasetActions from 'app/redux/actions/data/funnel/dataset'
import { businessManagerService } from '../../../app/service/business-manager'
import { BusinessConfig, BusinessError, BusinessManagerBusiness } from '@marketing-milk/interfaces'
import { NestAPI } from '../../../app/nestapi'

interface Props extends Redux, RouteComponentProps {}
interface State {
  loading: boolean
  checkingFbAccess: boolean
}

class Edit extends PureComponent<Props> {
  state: State = {
    loading: true,
    checkingFbAccess: false,
  }

  private mounted = false

  componentDidMount() {
    this.mounted = true
    this.fetch()
  }

  componentWillUnmount() {
    this.mounted = false
  }

  fetch = async () => {
    const { business }: any = this.props.match.params

    const result: BusinessManagerBusiness = await NestAPI.get(
      `businesses/manage/business/${business}`
    )
    await DatasetActions.loadBusiness(business)

    BusinessActions.init({
      ...result,
      id: business,
    })

    if (this.mounted) {
      this.setState({
        loading: false,
      })
    }

    // ad account/page permission checks are too slow (paginated, FB-rate-limited) to run as part of the
    // page load itself — kick them off in the background and update the Error Center once they resolve
    this.checkFbAccessInBackground(business)
  }

  // if the user navigates away before this resolves, the request and the redux update it drives should
  // still complete — only the local loading-spinner state and the "no issues found" toast are skipped,
  // since there's no page left to show them on
  checkFbAccessInBackground = (businessID: number) => {
    if (this.mounted) this.setState({ checkingFbAccess: true })
    businessManagerService
      .checkFbAccess(businessID)
      .then(errors => {
        BusinessActions.setErrors(errors)
        const hasFbAccessError =
          errors.includes(BusinessError.ad_account_access_error) ||
          errors.includes(BusinessError.page_access_error) ||
          errors.includes(BusinessError.lead_access_error)
        if (!hasFbAccessError && this.mounted) {
          toast.success('Facebook ad account and page access checked, no issues found', {
            autoClose: 5000,
          })
        }
      })
      .catch(() => {
        // best-effort — a failed background check just means the Error Center won't update this time;
        // the next save or page load will try again
      })
      .finally(() => {
        if (this.mounted) this.setState({ checkingFbAccess: false })
      })
  }

  onSubmit = async () => {
    const {
      pages: { business },
    } = this.props
    let toastId
    try {
      toastId = toast.info(
        <span>
          Saving business information <i className="fa fa-spinner fa-spin ml-2" />
        </span>,
        { autoClose: false }
      )
      // not worth here to attempt to detangle and solidify the redux interface. The Backend has very clear type-safe validation here
      const result = await businessManagerService.updateBusiness(
        business.id!,
        business as any as BusinessConfig
      )
      BusinessActions.setErrors(result.errors ?? null)
      this.checkFbAccessInBackground(business.id!)

      ErrorActions.clear()
      toast.update(toastId, {
        autoClose: 5000,
        type: toast.TYPE.SUCCESS,
        render: 'Business has been updated',
      })
    } catch (e: any) {
      if (e.response?.data?.data) {
        const { data } = e.response.data
        const errors = Array.isArray(data)
          ? data.map((d: string) => ({ column: '', message: d }))
          : Object.keys(data).map(x => ({
              column: x,
              message: Array.isArray(data[x]) ? `${x}: ${data[x][0]}` : data[x],
            }))
        ErrorActions.setErrors(errors)
      }
      toast.update(toastId, {
        autoClose: 5000,
        type: toast.TYPE.ERROR,
        render: 'Business could not be updated',
      })
    }
  }

  render() {
    const { loading, checkingFbAccess } = this.state
    return loading ? (
      <Loading />
    ) : (
      <Builder onSubmit={this.onSubmit} new={false} checkingFbAccess={checkingFbAccess} />
    )
  }
}

export default Connect(Edit)
